import { useEffect, useRef, useState } from 'react';
import { projectMemberSchema, type ProjectMember } from '@forecastify/shared';
import { useSession } from '../../session';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R3 · 3 Nov 2026 · who may own a cost or an invoice: the project's people. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE *FELELŐS* PICKER OFFERS THE PROJECT'S CURRENT, ACTIVE MEMBERS AND NOBODY ELSE** — the
 * server refuses anybody else with `assignee_not_a_project_member` (R3) or `member_not_active`
 * (R2), and offering a name the server will refuse is worse than not offering it. Both dialogs used
 * to offer the whole workspace from `/people`, deactivated people included.
 *
 * ⚠️ **The record's CURRENT owner stays in the list** (`keepCurrent`) even when they have left the
 * project or the company since: a picker missing the value it holds draws blank, and a form that
 * re-saves would read as clearing them.
 */
export interface OwnerChoice {
  readonly id: string;
  readonly name: string;
}

/** Whom a project member row may be offered for: today's window, and not deactivated. */
export function isOfferableMember(member: Pick<ProjectMember, 'active' | 'memberStatus'>): boolean {
  return member.active && member.memberStatus === 'active';
}

/** The offerable people of a project member list, one row per person. */
export function ownerChoicesOf(members: readonly ProjectMember[]): readonly OwnerChoice[] {
  const seen = new Map<string, OwnerChoice>();
  for (const member of members) {
    if (!isOfferableMember(member) || seen.has(member.memberId)) continue;
    seen.set(member.memberId, { id: member.memberId, name: member.displayName });
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * `options`, with the value the record already holds kept at the front when it is not among them —
 * named from `everyone` (a list that still has them), or not at all when nobody knows the name.
 */
export function keepCurrent<T extends { readonly id: string }>(
  options: readonly T[],
  currentId: string,
  everyone: readonly T[],
): readonly T[] {
  if (currentId === '' || options.some((one) => one.id === currentId)) return options;
  const current = everyone.find((one) => one.id === currentId);
  return current === undefined ? options : [current, ...options];
}

/**
 * The sentence for a refused cost or invoice save — R1, R2 and R3's codes by name, everything else
 * the generic one. ⚠️ Branching on `code`, never on `message` (rule 5).
 */
export async function ownerOrLineItemRefusal(
  t: Translate,
  response: Response | null,
): Promise<string> {
  const envelope = (await response?.json().catch(() => null)) as {
    error?: { code?: string; details?: { message?: string }[] };
  } | null;
  const code = envelope?.error?.code;
  const name = envelope?.error?.details?.[0]?.message ?? '';
  if (code === 'line_item_closed') return t('error.line_item_closed', { name });
  if (code === 'member_not_active') return t('error.member_not_active', { name });
  if (code === 'assignee_not_a_project_member') {
    return t('error.owner_not_a_project_member', { name });
  }
  /* ⚠️ A stale write is its own sentence: an in-place edit races the live reload. */
  return response?.status === 409 ? t('error.conflict') : t('error.unavailable');
}

/**
 * The owner choices for `projectId`, read from `/projects/:id/members`. `null` while unknown — no
 * project yet, a read in flight, or a read refused — so a caller can fall back rather than draw an
 * empty picker.
 */
export function useProjectOwnerChoices(projectId: string | null): readonly OwnerChoice[] | null {
  const session = useSession();
  /* ⚠️ Through a ref: `session` is a fresh object every render (C20's ten-requests bug). */
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const workspaceId = session.workspaceId;
  const [choices, setChoices] = useState<{
    readonly projectId: string;
    readonly list: readonly OwnerChoice[];
  } | null>(null);

  useEffect(() => {
    if (projectId === null || projectId === '' || workspaceId === null) return;
    const alive = { current: true };
    void (async () => {
      const response = await sessionRef.current
        .request(`/projects/${projectId}/members`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = projectMemberSchema.array().safeParse(body?.data);
      if (parsed.success && alive.current) {
        setChoices({ projectId, list: ownerChoicesOf(parsed.data) });
      }
    })();
    return () => {
      alive.current = false;
    };
  }, [projectId, workspaceId]);

  return choices !== null && choices.projectId === projectId ? choices.list : null;
}
